import React from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { FaChalkboardTeacher, FaGraduationCap, FaThLarge, FaUserGraduate, FaUsers, FaVideo } from 'react-icons/fa';
import AdminLayout from '../Admin/components/AdminLayout';
import { ShellGate } from '../../components/common';
import { useAuth } from '../../context/AuthContext';

/** Shared teacher nav - also used by Course Studio so the sidebar never drifts. */
export const TEACHER_NAV = [
  { to: '/dashboard/teacher', label: 'Dashboard', icon: FaThLarge },
  { to: '/dashboard/teacher/students', label: 'Students', icon: FaUsers },
  { to: '/admin/studio', label: 'Course Studio', icon: FaVideo },
];

/** Shared student nav - also used by Course Studio so the sidebar never drifts. */
export const STUDENT_NAV = [
  { to: '/dashboard/student', label: 'My Learning', icon: FaGraduationCap },
];

const NAV = {
  teacher: TEACHER_NAV,
  student: STUDENT_NAV,
};

const META = {
  teacher: { panelLabel: 'Teacher Portal', roleLabel: 'Teacher', userIcon: FaChalkboardTeacher },
  student: { panelLabel: 'Student Portal', roleLabel: 'Student', userIcon: FaUserGraduate },
};

/** Same dashboard shell for teachers and students: sidebar, header, auth guard. */
const DashboardShell = ({ role, title, subtitle, headerActions = null, children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const meta = META[role] || META.student;

  if (!isLoading && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (isLoading) {
    return (
      <ShellGate title="Loading…" body="Getting your dashboard ready.">
        <span className="inline-block w-5 h-5 rounded-full border-2 border-gray-200 border-t-designer-primary animate-spin" aria-label="Loading" />
      </ShellGate>
    );
  }

  const items = (NAV[role] || NAV.student).map((item) => ({
    ...item,
    active: location.pathname === item.to,
  }));

  return (
    <AdminLayout
      activeTab={null}
      onTabChange={() => {}}
      onLogout={() => { logout(); navigate('/login'); }}
      username={user?.name}
      roleLabel={meta.roleLabel}
      userIcon={meta.userIcon}
      panelLabel={meta.panelLabel}
      title={title}
      subtitle={subtitle}
      showTabs={false}
      navItems={items}
      headerActions={headerActions}
    >
      {children}
      <div className="mt-8 text-center">
        <Link to="/" className="text-xs text-gray-400 hover:text-designer-primary transition-colors">
          ← Back to Website
        </Link>
      </div>
    </AdminLayout>
  );
};

export default DashboardShell;
